import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  ArrowLeft, Bell, CheckCircle, CheckCheck, Heart, 
  MessageCircle, Video, CreditCard, Shield, Settings,
  Trash2, Filter, Calendar, Star, Lightbulb, Gift,
  Users, TrendingUp, Zap
} from 'lucide-react';
import axios from 'axios';

const NotificationsPage = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [creatingWelcome, setCreatingWelcome] = useState(false);

  useEffect(() => {
    loadNotifications();
    loadUnreadCount();
  }, [filter]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const unreadOnly = filter === 'unread';
      
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/notifications?limit=100&unread_only=${unreadOnly}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data && response.data.notifications) {
        let filteredNotifications = response.data.notifications;
        
        // Apply additional filters
        if (filter === 'matches') {
          filteredNotifications = response.data.notifications.filter(n => 
            n.type.includes('match') || n.type.includes('like')
          );
        } else if (filter === 'messages') {
          filteredNotifications = response.data.notifications.filter(n => 
            n.type.includes('message') || n.type.includes('chat')
          );
        }
        
        setNotifications(filteredNotifications);
        
        // If no notifications and this is the first load, create welcome notifications
        if (filteredNotifications.length === 0 && filter === 'all' && !creatingWelcome) {
          createWelcomeNotifications();
        }
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
      // If it's a 404 or the user has no notifications, show welcome
      if (error.response?.status === 404 || notifications.length === 0) {
        createWelcomeNotifications();
      }
    } finally {
      setLoading(false);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/notifications/unread-count`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data) {
        setUnreadCount(response.data.unread_count);
      }
    } catch (error) {
      console.error('Error loading unread count:', error);
    }
  };

  const createWelcomeNotifications = async () => {
    if (creatingWelcome) return;
    
    setCreatingWelcome(true);
    try {
      const token = localStorage.getItem('token');
      
      // Create welcome notifications via the dedicated endpoint
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/notifications/welcome`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      console.log('Welcome notifications created:', response.data);
      
      // Reload notifications after creating them
      setTimeout(() => {
        loadNotifications();
        loadUnreadCount();
      }, 1500);
      
    } catch (error) {
      console.error('Error creating welcome notifications:', error);
      // If the welcome endpoint fails, show a helpful message instead of leaving blank
      setNotifications([
        {
          id: 'temp-welcome',
          type: 'welcome',
          title: '🎉 Welcome to Shot Clock Dating!',
          message: 'Your dating journey starts here. Complete your profile and start meeting amazing people!',
          is_read: false,
          created_at: new Date().toISOString(),
          action_url: '/profile',
          action_label: 'Get Started'
        }
      ]);
    } finally {
      setCreatingWelcome(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/notifications/${notificationId}/read`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Update local state
      setNotifications(prev => 
        prev.map(n => 
          n.id === notificationId ? { ...n, is_read: true } : n
        )
      );
      loadUnreadCount();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/notifications/read-all`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setNotifications(prev => 
        prev.map(n => ({ ...n, is_read: true }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    // Mark as read when clicked
    if (!notification.is_read) {
      markAsRead(notification.id);
    }

    // Navigate to action URL if available
    if (notification.action_url) {
      navigate(notification.action_url);
    }
  };

  const getNotificationIcon = (type) => {
    const iconProps = { className: "h-6 w-6" };
    
    if (type.includes('match')) return <Heart {...iconProps} className="h-6 w-6 text-red-500" />;
    if (type.includes('message')) return <MessageCircle {...iconProps} className="h-6 w-6 text-blue-500" />;
    if (type.includes('video')) return <Video {...iconProps} className="h-6 w-6 text-purple-500" />;
    if (type.includes('payment')) return <CreditCard {...iconProps} className="h-6 w-6 text-green-500" />;
    if (type.includes('safety') || type.includes('profile')) return <Shield {...iconProps} className="h-6 w-6 text-yellow-500" />;
    if (type.includes('welcome')) return <Gift {...iconProps} className="h-6 w-6 text-pink-500" />;
    if (type.includes('tips')) return <Lightbulb {...iconProps} className="h-6 w-6 text-orange-500" />;
    if (type.includes('feature')) return <Star {...iconProps} className="h-6 w-6 text-indigo-500" />;
    if (type.includes('upgrade')) return <TrendingUp {...iconProps} className="h-6 w-6 text-emerald-500" />;
    
    return <Bell {...iconProps} className="h-6 w-6 text-gray-500" />;
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const notificationTime = new Date(timestamp);
    const diffMs = now - notificationTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return notificationTime.toLocaleDateString();
  };

  const filteredNotifications = notifications.filter(notification => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !notification.is_read;
    if (filter === 'matches') return notification.type.includes('match');
    if (filter === 'messages') return notification.type.includes('message');
    return true;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-x-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(236,72,153,0.1),transparent_70%)]"></div>
      
      <div className="relative z-10 max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button
            onClick={() => navigate(-1)}
            variant="outline"
            className="bg-white/10 border-white/20 text-white hover:bg-white/20"
          >
            <ArrowLeft className="mr-2" size={16} />
            Back
          </Button>
          
          <div className="text-center">
            <h1 className="text-3xl font-bold text-white flex items-center">
              <Bell className="mr-3 text-rose-500" size={32} />
              Notifications
              {unreadCount > 0 && (
                <Badge className="ml-3 bg-red-500 text-white">
                  {unreadCount}
                </Badge>
              )}
            </h1>
          </div>
          
          <Button
            onClick={() => navigate('/notifications/settings')}
            variant="outline"
            className="bg-white/10 border-white/20 text-white hover:bg-white/20"
          >
            <Settings className="mr-2" size={16} />
            Settings
          </Button>
        </div>

        {/* Filter Tabs */}
        <Card className="bg-white/5 backdrop-blur-md border-white/10 mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex space-x-2">
                {[
                  { id: 'all', label: 'All', count: notifications.length, icon: Bell },
                  { id: 'unread', label: 'Unread', count: unreadCount, icon: Zap },
                  { id: 'matches', label: 'Matches', count: notifications.filter(n => n.type.includes('match')).length, icon: Heart },
                  { id: 'messages', label: 'Messages', count: notifications.filter(n => n.type.includes('message')).length, icon: MessageCircle }
                ].map((tab) => {
                  const IconComponent = tab.icon;
                  return (
                    <Button
                      key={tab.id}
                      onClick={() => setFilter(tab.id)}
                      variant={filter === tab.id ? 'default' : 'outline'}
                      className={
                        filter === tab.id 
                          ? 'bg-rose-500 hover:bg-rose-600' 
                          : 'bg-white/10 border-white/20 text-white hover:bg-white/20'
                      }
                      size="sm"
                    >
                      <IconComponent className="w-4 h-4 mr-2" />
                      {tab.label}
                      {tab.count > 0 && <Badge className="ml-2 text-xs">{tab.count}</Badge>}
                    </Button>
                  );
                })}
              </div>
              
              {unreadCount > 0 && (
                <Button
                  onClick={markAllAsRead}
                  variant="outline"
                  className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                  size="sm"
                >
                  <CheckCheck className="w-4 h-4 mr-2" />
                  Mark all read
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Notifications List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-2 border-rose-500 border-t-transparent mx-auto mb-4"></div>
              <p className="text-white">Loading notifications...</p>
            </div>
          </div>
        ) : filteredNotifications.length > 0 ? (
          <div className="space-y-4">
            {filteredNotifications.map((notification) => (
              <Card 
                key={notification.id}
                className={`bg-white/5 backdrop-blur-md border-white/10 hover:border-white/20 transition-all duration-300 cursor-pointer ${
                  !notification.is_read ? 'border-l-4 border-l-rose-500 bg-rose-500/5' : ''
                }`}
                onClick={() => handleNotificationClick(notification)}
              >
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    {/* Icon */}
                    <div className="flex-shrink-0 mt-1">
                      {getNotificationIcon(notification.type)}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className={`text-lg ${!notification.is_read ? 'font-semibold text-white' : 'text-gray-200'}`}>
                            {notification.title}
                          </h3>
                          <p className="text-gray-300 mt-1">
                            {notification.message}
                          </p>
                          <div className="flex items-center justify-between mt-3">
                            <span className="text-sm text-gray-400">
                              {getTimeAgo(notification.created_at)}
                            </span>
                            
                            {notification.action_label && (
                              <Badge className="bg-rose-500/20 text-rose-300 border-rose-500/30">
                                {notification.action_label}
                              </Badge>
                            )}
                          </div>
                        </div>
                        
                        {/* Read status */}
                        <div className="ml-4 flex-shrink-0">
                          {!notification.is_read ? (
                            <div className="w-3 h-3 bg-rose-500 rounded-full"></div>
                          ) : (
                            <CheckCheck className="h-5 w-5 text-green-400" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="bg-white/5 backdrop-blur-md border-white/10">
            <CardContent className="p-12 text-center">
              <Bell className="w-20 h-20 text-gray-500 mx-auto mb-6" />
              <h3 className="text-2xl font-semibold text-white mb-4">
                {filter === 'all' ? 'No notifications yet' : `No ${filter} notifications`}
              </h3>
              <p className="text-gray-300 mb-8 max-w-md mx-auto">
                {filter === 'all' 
                  ? "We'll notify you when something interesting happens! Start by completing your profile or joining a speed dating session."
                  : `You don't have any ${filter} notifications at the moment. Check back later!`
                }
              </p>
              
              {filter === 'all' && (
                <div className="grid md:grid-cols-2 gap-4 max-w-md mx-auto">
                  <Button
                    onClick={() => navigate('/profile')}
                    className="bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600"
                  >
                    <Users className="w-4 h-4 mr-2" />
                    Complete Profile
                  </Button>
                  
                  <Button
                    onClick={() => navigate('/speed-dating')}
                    className="bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600"
                  >
                    <Video className="w-4 h-4 mr-2" />
                    Start Dating
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;